import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import Dialog from '../components/Dialog';
import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DateTimePicker from '../components/DateTimePicker';

import useDiaryData from '../hooks/useDiaryData';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useMainContext from '../hooks/useMainContext';

import birthdates from '../api/birthdates';
import { maxWidth } from '../components/configs';

const queryParamKey = 'update-birthdate';
const dateFormat = 'YYYY-MM-DD';

const generateDefaultState = () => {
	return {
		date: '',
		name: '',
		contact: '',
	}
}

const useStyles = makeStyles(theme => ({
	container: {
		maxWidth,
		margin: 'auto',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 10,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	},
	deleteButton: {
		width: '100%',
		marginTop: 10,
		marginBottom: 20,
		borderRadius: 6,
		fontWeight: 600,
		color: theme.palette.error.main,
	},
	loading: {
		marginTop: 50,
		marginBottom: 50,
		color: '#c7c7c7'
	},
	dialogContentWrapper: {
		display: 'flex',
		justifyContent: 'space-evenly',
		marginTop: 10,
		marginBottom: 10,
	}
}));

function UpdateBirthdateDialog() {
	const classes = useStyles();
	// eslint-disable-next-line
	const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
	const [queryParams, setQueryParams] = useQueryParams();
	const [disabled, setDisabled] = useState(false);
	const [deleteDisabled, setDeleteDisabled] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
	const [error, setError] = useState({});
	const handleSnackbarChange = useSnackbarText();
	const { birthdateDetailUpdate, handleChange: handleMainContextChange } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState) => {
		_setState(prevState => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleDateChange = (type) => (event, value) => {
		setState({ [type]: value });
	};

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.name) {
			noError = false;
			error.name = 'Invalid value.';
		}

		if(!state.date) {
			noError = false;
			error.date = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			return handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
		} 

		setDisabled(true);
		const { data, success } = await birthdates.updateBirthdate({
			birthdateID: birthdateDetailUpdate.birthdateID,
			name: state.name,
			date: new Date(state.date),
			contact: state.contact,
		});
		setDisabled(false);

		if(!success) {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		} else {
			handleSnackbarChange({ text: 'Birthdate updated', variant: 'success' });
			handleManualFetch();
			setQueryParams({ [queryParamKey]: null });
		}
	};

	const handleDeleteBirthdate = async () => {
		setDeleteDisabled(true);
		setQueryParams({ 'delete-birthdate': null });

		const { success, data } = await birthdates.deleteBirthdate({
			birthdateID: birthdateDetailUpdate.birthdateID,
		});
		setDeleteDisabled(false);

		if(!success) {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		}

		handleSnackbarChange({ text: 'Birthdate deleted', variant: 'success' });
		handleManualFetch();
		setQueryParams({ [queryParamKey]: null });
		handleMainContextChange({ birthdateDetailUpdate: null });
	};

	const handleDeletePress = () => {
		setQueryParams({ 'delete-birthdate': 'true' });
	};

	useEffect(() => {
		_setState(generateDefaultState(diary.selectedDate));
		if(!queryValue) handleMainContextChange({ birthdateDetailUpdate: null });
	}, [queryValue, diary.selectedDate, handleMainContextChange]);

	useEffect(() => {
		if(!birthdateDetailUpdate) {
			handleMainContextChange({ birthdateDetailUpdate: null });
			return setQueryParams({ [queryParamKey]: null });
		}

		const birthdateID = queryValue;

		const getData = async () => {
			let { data, success } = await birthdates.getBirthdate({ birthdateID });

			if(success) {
				setLoaded(true);
				const birthdateDetails = data.birthdate;
				setState({
					date: moment(birthdateDetails.date).format(dateFormat),
					name: birthdateDetails.name,
					contact: String(birthdateDetails.contact),
				});
			} else {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			}
		};

		if(birthdateID) getData();
	}, [queryValue, birthdateDetailUpdate, setQueryParams, setState, handleSnackbarChange, handleMainContextChange]);

	return (
		<FullScreenDialog 
			title="Update Birthdate"
			queryParamKey={queryParamKey}
		>
			{!loaded && <div className={classes.loading}>loading</div>}
			{loaded && (
				<div className={classes.container}>
					<TextField 
						label="Name*"
						value={state.name}
						onChange={handleChange('name')}
						error={Boolean(error.name)}
						helperText={error.name}
					/>

					<TextField 
						label="Contact"
						value={state.contact}
						onChange={handleChange('contact')}
					/>

					<DateTimePicker 
						label="Date"
						value={state.date}
						onChange={handleDateChange('date')}
						error={Boolean(error.date)}
						helperText={error.date}
					/>

					<Button 
						className={classes.submitButton}
						onClick={handleSubmit}
						disabled={disabled}
					>
						Update
					</Button>
					<Button 
						className={classes.deleteButton}
						onClick={handleDeletePress}
						disabled={deleteDisabled}
					>
						Delete
					</Button>
				</div>
			)}

			{/* Todo delete confirm */}
			<Dialog title="Delete this birthdate?" queryParamKey="delete-birthdate">
				<div className={classes.dialogContentWrapper}>
					<Button 
						variant="contained" 
						color="primary" 
						onClick={handleDeleteBirthdate}
					>
						Delete
					</Button>
					<Button onClick={() => setQueryParams({ 'delete-birthdate': null })}>
						Cancel
					</Button>
				</div>
			</Dialog>
		</FullScreenDialog>
	);
}

export default UpdateBirthdateDialog;
